import { Landmark, Lock, ArrowUpRight, ReceiptText, FileSpreadsheet, ShieldCheck, KeyRound, Globe2 } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import AppCard from "@/components/AppCard";
import { APPS, RESOURCES, CONTACT } from "@/lib/content";
import { useSiteContent } from "@/lib/useSiteContent";

const BAND_IMG =
  "https://images.unsplash.com/photo-1652106032456-fc4ce369b1c9?q=80&w=2000&auto=format&fit=crop";

const toneStyles = {
  open: "bg-[#3682AE]/10 text-[#3682AE] border-[#3682AE]/30",
  notice: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  closed: "bg-stone-500/10 text-stone-600 border-stone-500/25",
};

export default function ClientCentre() {
  const { tax_season: ts } = useSiteContent();
  const tone = toneStyles[ts?.status_tone] || toneStyles.open;

  return (
    <div data-testid="client-centre-page" className="pb-24">
      {/* Header band */}
      <section className="relative overflow-hidden bg-[#102036] pt-[72px]" data-testid="client-centre-hero">
        <img src={BAND_IMG} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#102036]/70 via-[#102036]/80 to-[#102036]" aria-hidden="true" />
        <div className="grain absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1320px] px-5 py-20 sm:px-8 lg:py-28">
          <Eyebrow light>Ford &amp; Associates</Eyebrow>
          <h1 className="mt-4 font-display text-5xl font-medium tracking-tight text-[#FFFFFF] sm:text-6xl">Client Centre</h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#FFFFFF]/75 sm:text-lg">
            Everything you need to work with Ford &amp; Associates — in one place.
          </p>
        </div>
      </section>

      {/* Primary applications */}
      <section className="mx-auto max-w-[1320px] px-5 pt-20 sm:px-8" data-testid="client-centre-apps">
        <Reveal>
          <Eyebrow>Client applications</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-[#102036] sm:text-4xl">
            Sign in to your secure portals.
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <Reveal delay={0.05}>
            <div className="relative rounded-lg bg-gradient-to-br from-[#102036] to-[#3682AE] p-1 shadow-[0_24px_60px_rgb(12,24,42,0.22)]">
              <div className="rounded-[7px] bg-white">
                <AppCard app={APPS.clientManager} large testid="cc-client-manager-card" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-lg bg-gradient-to-br from-[#102036] to-[#3682AE] p-1 shadow-[0_24px_60px_rgb(12,24,42,0.22)]">
              <div className="rounded-[7px] bg-white">
                <AppCard app={APPS.taxManager} large testid="cc-tax-manager-card" />
              </div>
            </div>
          </Reveal>
        </div>
        <p className="mt-5 flex items-center gap-2 text-xs text-[#4A5568]/80">
          <Lock className="h-3.5 w-3.5" /> Both applications open in a new tab on their own secure domains. We never imitate their sign-in on this website.
        </p>
      </section>

      {/* Tax Season Centre */}
      <section className="mx-auto mt-24 max-w-[1320px] px-5 sm:px-8" data-testid="tax-season-centre">
        <Reveal>
          <div className="overflow-hidden rounded-lg border border-[#102036]/10 bg-white shadow-[0_8px_30px_rgb(12,24,42,0.05)]">
            <div className="flex flex-col gap-4 border-b border-[#102036]/10 bg-[#FFFFFF] px-8 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-10">
              <div>
                <Eyebrow>Tax Season Centre</Eyebrow>
                <h2 className="mt-2 font-display text-3xl font-medium tracking-tight text-[#102036]">This year's key information</h2>
              </div>
              <span data-testid="tax-season-status" className={`inline-flex w-fit items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] ${tone}`}>
                <span className="relative flex h-2 w-2">
                  <span className={`absolute h-full w-full animate-ping rounded-full opacity-60 ${ts?.status_tone === "open" ? "bg-[#3682AE]" : "bg-stone-500"}`} />
                  <span className={`h-2 w-2 rounded-full ${ts?.status_tone === "open" ? "bg-[#3682AE]" : "bg-stone-500"}`} />
                </span>
                {ts?.status || "Open"}
              </span>
            </div>

            <div className="px-8 py-9 sm:px-10">
              <p className="max-w-3xl text-lg leading-relaxed text-[#102036]/85" data-testid="tax-season-headline">{ts?.headline}</p>

              <div className="mt-10 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#4A5568]/70">Key dates</p>
                  <ul className="mt-5 divide-y divide-[#102036]/10" data-testid="tax-season-dates">
                    {(ts?.dates || []).map(([label, value], i) => (
                      <li key={`${label}-${i}`} className="flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-[15px] text-[#102036]/85">{label}</span>
                        <span className="font-display text-xl font-semibold text-[#102036]">{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#4A5568]/70">Notices</p>
                  <ul className="mt-5 space-y-4" data-testid="tax-season-notices">
                    {(ts?.notices || []).map((n, i) => (
                      <li key={i} className="flex gap-3 rounded-md border border-[#102036]/10 bg-[#FFFFFF] p-4 text-sm leading-relaxed text-[#102036]/80">
                        <ReceiptText className="mt-0.5 h-4 w-4 shrink-0 text-[#102036]" />
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {ts?.instructions && (
                <div className="mt-10 rounded-md bg-[#102036] p-7 text-[15px] leading-relaxed text-[#FFFFFF]/90" data-testid="tax-season-instructions">
                  <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#FFFFFF]/60">
                    <FileSpreadsheet className="h-4 w-4" /> How to get started
                  </p>
                  {ts.instructions}
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Payments */}
      <section className="mx-auto mt-24 max-w-[1320px] px-5 sm:px-8" data-testid="client-centre-payments">
        <Reveal>
          <Eyebrow>Payments</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-[#102036] sm:text-4xl">Pay an invoice online.</h2>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {[
            {
              icon: ReceiptText,
              title: "Pay an Invoice",
              desc: "View and pay your Ford & Associates invoices securely through Client Manager.",
              href: APPS.clientManager.url,
              cta: "Pay securely in Client Manager",
              testid: "cc-pay-invoice-card",
            },
            {
              icon: FileSpreadsheet,
              title: "T1 Tax Service Payment",
              desc: "Pay your personal tax service fees online through our secure client portal.",
              href: APPS.taxManager.url,
              cta: "Pay securely in Tax Manager",
              testid: "cc-t1-payment-card",
            },
          ].map((p) => (
            <Reveal key={p.title} delay={0.08}>
              <a
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={p.testid}
                className="group flex h-full items-center justify-between gap-6 rounded-lg border border-[#102036]/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#102036]/30 hover:shadow-[0_16px_40px_rgb(12,24,42,0.08)] sm:p-10"
              >
                <div>
                  <p className="font-display text-2xl font-semibold text-[#102036]">{p.title}</p>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-[#4A5568]">{p.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-[#102036]">
                    {p.cta}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
                <p.icon className="h-12 w-12 shrink-0 text-[#102036]/30 transition-colors group-hover:text-[#102036]" strokeWidth={1.25} />
              </a>
            </Reveal>
          ))}
        </div>
        <p className="mt-4 text-xs text-[#4A5568]/75">
          Card data is handled by our secure client portal — never collected on this public website.
        </p>
      </section>

      {/* CRA Resources */}
      <section className="mx-auto mt-24 max-w-[1320px] px-5 sm:px-8" data-testid="cra-resources">
        <Reveal>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <Eyebrow>CRA resources</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-[#102036] sm:text-4xl">Official government links.</h2>
            </div>
            <p className="flex items-center gap-2 text-xs text-[#4A5568]/80">
              <Globe2 className="h-4 w-4" /> Links open canada.ca in a new tab — you are leaving this website.
            </p>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {RESOURCES.map((r, i) => (
            <Reveal key={r.name} delay={0.04 * (i % 3)}>
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`cra-resource-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex h-full flex-col justify-between rounded-lg border border-[#102036]/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#102036]/30 hover:shadow-[0_14px_36px_rgb(12,24,42,0.07)]"
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-xl font-semibold text-[#102036]">{r.name}</h3>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-[#4A5568]/50 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#102036]" />
                  </div>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#4A5568]">{r.desc}</p>
                </div>
                <span className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#EAE7E0]/60 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#4A5568]">
                  <Landmark className="h-3 w-3" /> Government of Canada
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Security */}
      <section className="mx-auto mt-24 max-w-[1320px] px-5 sm:px-8" data-testid="client-centre-security">
        <Reveal>
          <div className="rounded-lg border border-[#102036]/10 bg-[#FFFFFF] p-10 sm:p-12">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-[#3682AE]" />
              <h2 className="font-display text-3xl font-medium tracking-tight text-[#102036]">Your security, by design.</h2>
            </div>
            <div className="mt-8 grid gap-8 md:grid-cols-3">
              {[
                { icon: Lock, title: "Encrypted portals", text: "Client Manager and Tax Manager are separate secure applications on their own domains, with their own authentication." },
                { icon: KeyRound, title: "No credentials here", text: "We never ask for portal passwords, card numbers or tax identifiers on this public website — beware of impostors." },
                { icon: Globe2, title: "Verified destinations", text: "Government links point only to official canada.ca resources, and open in a new tab so you always know where you are." },
              ].map((s) => (
                <div key={s.title}>
                  <s.icon className="h-5 w-5 text-[#3682AE]" strokeWidth={1.75} />
                  <h3 className="mt-4 text-[15px] font-bold text-[#102036]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#4A5568]">{s.text}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 border-t border-[#102036]/10 pt-6 text-xs text-[#4A5568]/75">
              Questions about a request you received? Call our office at{" "}
              <a href={CONTACT.phoneHref} data-testid="security-phone-link" className="font-semibold text-[#102036]">289-464-1001</a> before sharing anything.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
